import { useEffect, useState } from 'react';
import React from 'react';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import { Form } from '@jht/react-library';
import { makeStyles } from '@material-ui/core/styles';
import { ProductPricing } from './ProductPricing';
import { observer } from 'mobx-react';
import { useMst } from '../../core/stores';
import { isFacilityKeyedProductDetail } from '../../libs/static/productsAndIntegrations.enum';
import { reindexFacilityDetails } from '../../libs/helpers/facilityKeys';

const useStyles = makeStyles(() => ({
  container: {
    paddingBottom: 0,
  },
}));

export const Product = observer(({ name, title, onChange, values, subFormSchema, facilities }) => {
  const classes = useStyles();
  const { digitalOrdersStore } = useMst();
  const facilityNames = facilities.map(f =>
    f.facilityId ? digitalOrdersStore.facilityMap.get(f.facilityId).name : f.name,
  );

  const facilityExistence = facilities.map(f => !!f.facilityId);

  const blankData = subFormName => {
    if (isFacilityKeyedProductDetail(subFormName)) {
      return reindexFacilityDetails(undefined, facilityNames);
    }
    return {};
  };

  const checkSubform = (subFormName, currentData) => {
    if (isFacilityKeyedProductDetail(subFormName) && currentData) {
      // Matches on a normalized facility key so orders saved before DO-295, whose keys humps
      // mangled ("Gym A" -> "gymA"), still resolve onto the order's current facility names.
      return reindexFacilityDetails(currentData, facilityNames);
    }
    return currentData;
  };

  const [checked, setChecked] = useState(values.checkbox || false);
  const [subForm, setSubForm] = useState(
    subFormSchema
      ? subFormSchema(
          checkSubform(name, values.subform) || blankData(name),
          facilityNames,
          facilityExistence,
        )
      : null,
  );
  const [formValues, setFormValues] = useState(checkSubform(name, values.subform || undefined));

  useEffect(() => {
    if (!checked) {
      setFormValues(undefined);
    }
  }, [checked]);

  const extractValues = elements => {
    return elements.reduce((memo, current) => {
      if (
        current.name === 'iFitGrid' ||
        current.name === 'facility360Grid' ||
        current.name === 'amVisionGrid'
      ) {
        memo = current.value;
      } else if (current.inputType === 'checkbox') {
        memo[current.name] = current.checked || false;
      } else if (current.value) {
        memo[current.name] = current.value;
      }
      return memo;
    }, {});
  };

  const handleCheckboxChange = ({ target }) => {
    setChecked(target.checked);
    const obj: any = {
      [name]: {
        checkbox: target.checked,
      },
    };

    if (subForm) {
      // clear form if unchecking checkbox
      if (!target.checked) {
        setSubForm(subFormSchema(blankData(name), facilityNames, facilityExistence));
      }

      obj[name].subform = target.checked ? extractValues(subForm.elements) : {};
      obj[name].subformValid = target.checked ? subForm.valid : false;
    }

    onChange(obj);
  };

  const handleSubformChange = formState => {
    const formValues = extractValues(formState.elements);
    const prevPartner = subForm?.elements?.find(el => el.name === 'integrationPartner')?.value;
    // When the integration partner changes, rebuild the schema so only that
    // partner's fields are present. This drops the previous partner's values
    // and prevents hidden fields from reserving empty grid columns.
    if (subFormSchema && formValues.integrationPartner !== prevPartner) {
      const rebuilt = subFormSchema(
        { integrationPartner: formValues.integrationPartner },
        facilityNames,
        facilityExistence,
      );
      const rebuiltValues = extractValues(rebuilt.elements);
      setSubForm(rebuilt);
      setFormValues(rebuiltValues);
      onChange({
        [name]: {
          subform: rebuiltValues,
          subformValid: rebuilt.valid,
        },
      });
      return;
    }
    setSubForm(formState);
    setFormValues(formValues);
    onChange({
      [name]: {
        subform: formValues,
        subformValid: formState.valid,
      },
    });
  };

  return (
    <div className={classes.container}>
      <FormControlLabel
        key={name}
        control={
          <Checkbox checked={checked} onChange={handleCheckboxChange} name={name} color="primary" />
        }
        label={title}
      />
      {checked && subForm && (
        <Form onChange={handleSubformChange} onSubmit={() => {}} schema={subForm}>
          <></>
        </Form>
      )}
      {checked && (
        <ProductPricing productName={name} subformValues={formValues} facilities={facilities} />
      )}
    </div>
  );
});
